/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI
 * Copyright IBM Corp. 2020, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'react',
	'prop-types',
	'ba-ui-carbon',
	'./controls/SingleLineLink',
	'./controls/SectionTitle',
	'./controls/Separator',
	'./CredentialDialogs',
	'caglass/ajax/CAAuthentication',
	'../../nls/StringResources'
], (React, PropTypes, Carbon, SingleLineLink, SectionTitle, Separator, CredentialDialogs, CAAuthentication, StringResources) => {

	const IDENTITY_URL = 'v1/users/~/identity';

	const { Loading } = Carbon;

	return class ProfileTab extends React.Component {

		static propTypes = {
			Glass: PropTypes.any,
			slideout: PropTypes.any
		}

		constructor() {
			super(...arguments);
			this.state = {
				loading: true
			};
			this.credentialDialogs = new CredentialDialogs(this.props.Glass);
		}

		componentDidMount() {
			const userProfileSvc = this.props.Glass.getCoreSvc('.UserProfile');
			// Make sure product / content locales and time zones are loaded before render.
			return Promise.all([
				this._isCognosExNamespaceUser(),
				userProfileSvc.getCapabilities()
			]).then(values => {
				const [ isCognosExNamespaceUser, capabilities ] = values;
				this.setState({
					isCognosExNamespaceUser,
					capabilities,
					loading: false
				});
			});
		}

		_getItems() {
			const items = [];
			if ( !this.state.loading ) {
				const userProfileSvc = this.props.Glass.getCoreSvc('.UserProfile');

				items.push(<SectionTitle
					name="emailTitle"
					label={StringResources.get('email')}
				/>);

				items.push(<SingleLineLink
					name="email"
					label={userProfileSvc.userAccount.email}
				/>);

				if ( this.state.isCognosExNamespaceUser ) {
					items.push(<SingleLineLink
						name="changePassword"
						label={StringResources.get('password')}
						value={StringResources.get('passwordChange')}
						enabled={true}
						clickCallback={this._resetPassword.bind(this)}
					/>);
				}

				items.push(<Separator name="emailSeparator"/>);

				items.push(<SectionTitle
					name="advancedOptions"
					label={StringResources.get('advanced')}
				/>);

				items.push(<SingleLineLink
					name="renewCredentials"
					label={StringResources.get('credentials')}
					value={StringResources.get('credentialChange')}
					enabled={true}
					clickCallback={this._renewCredentials.bind(this)}
				/>);

				items.push(<SingleLineLink
					name="viewCredentials"
					label={StringResources.get('myCredentials')}
					value={StringResources.get('manage')}
					enabled={userProfileSvc.hasCredentials === true}
					clickCallback={this._openCredentialsViewSlideout.bind(this)}
				/>);

				items.push(<SingleLineLink
					name="apiKeys"
					label={StringResources.get('myApiKeys')}
					value={StringResources.get('manage')}
					enabled={true}
					clickCallback={this._openMyApiKeys.bind(this)}
				/>);

				if (this.props.Glass.hasCapability('canUsePersonalDataSourceCredentials')) {
					items.push(<SingleLineLink
						name="datasourceCredentials"
						label={StringResources.get('dataSourceCredentials')}
						value={StringResources.get('manage')}
						enabled={true}
						clickCallback={this._openDataSourceCredentialsViewSlideout.bind(this)}
					/>);
				}

				items.push(<SingleLineLink
					name="myGroupsAndRoles"
					label={StringResources.get('groupsAndRoles')}
					value={StringResources.get('viewDetails')}
					enabled={true}
					clickCallback={this._openGroupsAndRolesViewSlideout.bind(this)}
				/>);

				items.push(<SingleLineLink
					name="myCapabilities"
					label={StringResources.get('myCapabilities')}
					value={StringResources.get('viewDetails')}
					enabled={true}
					clickCallback={this._openCapabilitesViewSlideout.bind(this)}
				/>);

				items.push(<SingleLineLink
					name="editLoggingConfig"
					label={StringResources.get('manageLogging')}
					value={StringResources.get('manage')}
					enabled={true}
					clickCallback={this._openLoggingConfigSlideout.bind(this)}
				/>);

				if( this.props.Glass.hasCapability('canLearnFromProductUsage') ){
					items.push(<SingleLineLink
						name="editAILearningConfig"
						label={StringResources.get('manageAILearning')}
						value={StringResources.get('manage')}
						enabled={true}
						clickCallback={this._openAILearningConfigSlideout.bind(this)}
					/>);
				}
			}
			return items;
		}

		render() {
			return (
				<div className="profileTab">
					{this.state.loading && <Loading
						description={StringResources.get('loadingProfile')}
						withOverlay={false}
					/>}
					{this._getItems()}
				</div>
			);
		}

		_isCognosExNamespaceUser() {
			return this.props.Glass.getCoreSvc('.Ajax').ajax({
				type: 'GET',
				'headers': {
					'Accept': 'application/json',
					'Content-Type': 'application/json'
				},
				'url': IDENTITY_URL
			}).then(response => {
				if (response && response.data) {
					const namespaceObjects = response.data.data;
					for (let i=0; i < namespaceObjects.length; i++) {
						if (namespaceObjects[i].type == 'namespace' && namespaceObjects[i].searchPath == 'CAMID("CognosEx")') {
							return true;
						}
					}
				}
				return false;
			});
		}

		_renewCredentials() {
			const caAuthentication = new CAAuthentication();
			return caAuthentication.storeCredentials(this.props.Glass).then(() => {
				return this.props.Glass.showToast(StringResources.get('credentialRenewed'));
			}, error => {
				if (error && error.displayObjects) {
					return this.credentialDialogs.openRenewCredentialDialog(error.displayObjects);
				}
			});
		}

		_resetPassword() {
			return this.credentialDialogs.openResetPasswordDialog();
		}

		_openMyApiKeys() {
			this.props.Glass.openAppView('apiKeys');
		}

		_openAILearningConfigSlideout() {
			this.props.slideout.addChild({
				glassContext: this.props.Glass,
				enableTabLooping: true,
				overlay: true,
				label: StringResources.get('AILearningConfigurationSlideoutAllyLabel'),
				global: !!this.props.slideout.global,
				content: {
					module: 'ca_portal/features/mypreferences/AILearningConfigSlideout',
					glassContext: this.props.Glass,
					userProfileSvc: this.props.Glass.getCoreSvc('.UserProfile')
				}
			});
		}

		_openLoggingConfigSlideout() {
			this.props.slideout.addChild({
				glassContext: this.props.Glass,
				enableTabLooping: true,
				overlay: true,
				label: StringResources.get('loggingConfigurationSlideoutA11yLabel'),
				global: !!this.props.slideout.global,
				content: {
					module: 'ca_portal/features/mypreferences/LoggingConfigSlideout',
					glassContext: this.props.Glass
				}
			});
		}

		_openCredentialsViewSlideout() {
			this.props.slideout.addChild({
				glassContext: this.props.Glass,
				enableTabLooping: true,
				overlay: true,
				global: !!this.props.slideout.global,
				content: {
					module: 'bi/content_apps/CredentialsView',
					glassContext: this.props.Glass
				}
			});
		}

		_openDataSourceCredentialsViewSlideout() {
			this.props.slideout.addChild({
				glassContext: this.props.Glass,
				enableTabLooping: true,
				overlay: true,
				global: !!this.props.slideout.global,
				content: {
					module: 'bi/content_apps/DataSourceCredentialsView',
					glassContext: this.props.Glass
				}
			});
		}

		_openGroupsAndRolesViewSlideout() {
			this.props.slideout.addChild({
				glassContext: this.props.Glass,
				enableTabLooping: true,
				overlay: true,
				global: !!this.props.slideout.global,
				content: {
					module: 'bi/content_apps/GroupsAndRolesView',
					glassContext: this.props.Glass
				}
			});
		}

		_openCapabilitesViewSlideout() {
			this.props.slideout.addChild({
				glassContext: this.props.Glass,
				enableTabLooping: true,
				overlay: true,
				global: !!this.props.slideout.global,
				content: {
					module: 'bi/content_apps/CapabilitiesView',
					glassContext: this.props.Glass
				}
			});
		}

	};

});